import React from "react";
import { useTranslation } from "react-i18next";
import { InlineSvg } from "./InlineSvg";
import { emptyCircleSvg, halfCircleSvg, fullCircleSvg, crossedCircleSvg } from "./svg";
import type { DelayBase } from "./hooks/useDelayBuckets";

type Props = {
  open: boolean;
  onClose: () => void;
  selectedDelayBase: DelayBase;
  setSelectedDelayBase: (b: DelayBase) => void;
  visible: Record<1|2|3|4, boolean>;
  setVisible: React.Dispatch<React.SetStateAction<Record<1|2|3|4, boolean>>>;
  panelRef: React.RefObject<HTMLDivElement>;
};

export const ControlsPanel: React.FC<Props> = ({
  open, onClose, selectedDelayBase, setSelectedDelayBase, visible, setVisible, panelRef
}) => {
  const { t } = useTranslation();

  return (
    <>
      {open && <button className="map-controls-backdrop" onClick={onClose} aria-label="Close settings" />}
      <div
        id="map-controls"
        ref={panelRef}
        className={`map-controls ${open ? "open" : "close"}`}
        role="dialog"
        aria-label="Map controls"
        aria-modal="false"
      >
        <div className="mc-header">
          <div className="mc-title">{t("TS_core:CalculateDelayFor") || "Calculate delay for"}</div>
        </div>

        <div className="mc-body">
          <div className="mc-section">
            <button
              className={`mc-option ${selectedDelayBase === 200 ? "active" : ""}`}
              onClick={() => setSelectedDelayBase(200)}
              title={t("TS_core:PositionTimeToMessageTime") || "Position → Message"}
            >
              <div className="mc-option-title"><strong>{t("TS_core:Onboard") || "Onboard"}</strong></div>
              <div className="mc-option-desc">{t("TS_core:PositionTimeToMessageTime") || "Position time → Message time"}</div>
            </button>

            <button
              className={`mc-option ${selectedDelayBase === 100 ? "active" : ""}`}
              onClick={() => setSelectedDelayBase(100)}
              title={t("TS_core:MessageTimeToTimeSaved") || "Message → Saved"}
            >
              <div className="mc-option-title">{t("TS_core:Offboard") || "Offboard"}</div>
              <div className="mc-option-desc">{t("TS_core:MessageTimeToTimeSaved") || "Message time → Time saved"}</div>
            </button>
          </div>

          <div className="mc-section">
            <div className="mc-subtitle">{t("TS_core:DisplayDelayLevels") || "Display delay levels"}</div>
            {([1,2,3,4] as const).map(b => (
              <label key={b} className="mc-check">
                <input
                  type="checkbox"
                  checked={visible[b]}
                  onChange={e => setVisible(v => ({ ...v, [b]: e.target.checked }))}
                />
                <InlineSvg html={[emptyCircleSvg(), halfCircleSvg(), fullCircleSvg(), crossedCircleSvg()][b-1]} />
                <span>
                  {b === 1 ? "10 min – 1 hour" :
                   b === 2 ? "1 hour – 1 day"  :
                   b === 3 ? "1 day – 14 days" : "> 14 days"}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};
